import React, { ButtonHTMLAttributes, ReactNode } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'

export const VARIANTS = Object.freeze({
  small: 'small',
  normal: 'normal',
  smallOutline: 'smallOutline',
  outline: 'outline',
  text: 'text',
  // text-only action, e.g. "Gutscheincode einlösen" or "Details anzeigen"
  link: 'link',
})

type Variant = typeof VARIANTS[keyof typeof VARIANTS]

interface ButtonSquareProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  type?: 'button' | 'submit' | 'reset'
  label?: string
  disabled?: boolean
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  customClass?: string
  className?: string
  children?: ReactNode
  variant?: Variant
  dataQsId: string
}

const isSmall = (variant: Variant) => variant === VARIANTS.small || variant === VARIANTS.smallOutline
const isOutline = (variant: Variant) => variant === VARIANTS.smallOutline || variant === VARIANTS.outline

// borderless, background-less look for secondary actions below a primary button
const textVariantStyles = (theme: ThemeInterface) => ({
  width: 'auto',
  height: 'auto',
  padding: 4,
  borderWidth: 0,
  background: 'transparent',
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  '&:hover': {
    background: 'transparent',
    color: theme.palette.text.primary,
  },
  '&:disabled': {
    background: 'transparent',
    color: theme.palette.text.disabled,
  },
})

// inline action in the brand colour
const linkVariantStyles = (theme: ThemeInterface) => ({
  height: 'auto',
  width: 'auto',
  padding: 0,
  borderWidth: 0,
  background: 'none',
  color: theme.palette.brand.main,
  fontSize: 'inherit',
  '&:hover': {
    background: 'none',
    color: theme.palette.brand.dark,
  },
  '&:disabled': {
    background: 'none',
    color: theme.palette.text.disabled,
  },
})

const useStyles = makeStyles((theme: ThemeInterface) => ({
  button: ({ variant }: { variant: Variant }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: !isSmall(variant) ? 45 : 'auto',
    padding: !isSmall(variant) ? '0px 20px' : '8px 20px',
    borderRadius: theme.borderRadius,
    borderWidth: !isOutline(variant) ? 0 : 1,
    outline: 'none',
    overflow: 'hidden',
    background: !isOutline(variant) ? theme.colorPrimaryButtonBackground : theme.white,
    color: !isOutline(variant) ? theme.white : theme.primary,
    width: !isSmall(variant) ? '100%' : 'auto',
    transition: 'background 0.3s ease-in-out',
    '&:hover': {
      background: !isOutline(variant) ? theme.colorPrimaryButtonBackgroundHover : theme.white,
      color: !isOutline(variant) ? theme.white : theme.primary,
    },
    '&:disabled': {
      background: theme.disabledColor,
    },
    ...(variant === VARIANTS.text && textVariantStyles(theme)),
    ...(variant === VARIANTS.link && linkVariantStyles(theme)),
  }),
}))

const ButtonSquare = (props: ButtonSquareProps) => {
  const {
    type = 'button',
    label,
    disabled = false,
    onClick,
    customClass,
    children,
    className,
    variant = VARIANTS.normal,
    dataQsId,
    ...otherProps
  } = props
  const classes = useStyles({ variant })

  return (
    <button
      type={type}
      disabled={disabled}
      className={clsx(classes.button, customClass, className)}
      onClick={onClick}
      data-qs-id={dataQsId}
      {...otherProps}>
      {label || children}
    </button>
  )
}

export default ButtonSquare
